// src/student/pages/StudentPrelims.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckSquare,
  Play,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { studentPortalAPI } from "../services/studentApi";

export default function StudentPrelims() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await studentPortalAPI.getPublishedTests();
      if (res.success && Array.isArray(res.data)) {
        setTests(res.data);
      }
    } catch (err) {
      setError(err.message || "Failed to load Prelims tests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-red-950 via-slate-900 to-[#0b1528] rounded-2xl p-6 text-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-white/10 px-2.5 py-0.5 rounded-full">
              Free UPSC Initiative
            </span>
            <span className="text-xs text-slate-300 font-medium">MCQ Mock Portal</span>
          </div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight">
            Prelims Mock Tests
          </h1>
          <p className="text-xs text-slate-300 mt-1 max-w-xl">
            Simulate actual UPSC CSE Prelims conditions with live server-side timer, negative marking (+2.00, -0.66), and instant performance scorecards.
          </p>
        </div>

        <button
          onClick={fetchTests}
          className="self-start md:self-auto flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold backdrop-blur-xs transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span>Refresh Tests</span>
        </button>
      </div>

      {/* UPSC Guidelines Warning Card */}
      <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-4 flex items-start gap-3">
        <ShieldAlert className="text-amber-700 flex-shrink-0 mt-0.5" size={18} />
        <div className="text-xs text-amber-900 space-y-1">
          <p className="font-bold">Instructions & Examination Rules</p>
          <p className="text-amber-800 leading-relaxed">
            1. Each question carries +2.00 marks for a correct answer, and -0.66 marks for an incorrect answer. Unanswered questions receive 0.00.
          </p>
          <p className="text-amber-800 leading-relaxed">
            2. The countdown timer runs continuously on the server. Your test will auto-submit when the duration expires.
          </p>
        </div>
      </div>

      {/* Tests Grid */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">
          Loading Prelims Tests...
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      ) : tests.length === 0 ? (
        <div className="py-16 bg-white rounded-2xl border border-slate-200 text-center">
          <CheckSquare className="mx-auto text-slate-300 mb-2" size={36} />
          <p className="text-sm font-semibold text-slate-700">No active Prelims tests available</p>
          <p className="text-xs text-slate-400 mt-1">Please check back soon or consult the test calendar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {tests.map((test) => {
            const totalQ = test.totalQuestions || test.questions?.length || 0;
            return (
              <div
                key={test._id}
                className="bg-white rounded-2xl border border-slate-200/90 hover:border-red-800/40 hover:shadow-lg transition-all duration-200 flex flex-col justify-between overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                      Active Mock
                    </span>
                    <span className="text-[11px] text-slate-400 font-medium">
                      CSE GS Paper-I
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2">
                    {test.title}
                  </h3>

                  <p className="text-xs text-slate-600 line-clamp-2 mb-4 leading-relaxed">
                    {test.description || "Comprehensive test containing curated questions from multiple subject domains."}
                  </p>

                  {/* Test Specs Grid */}
                  <div className="grid grid-cols-3 gap-2 py-3 px-3 rounded-xl bg-slate-50 border border-slate-100 text-center text-xs">
                    <div>
                      <span className="block text-[10px] text-slate-400 uppercase font-semibold">Questions</span>
                      <span className="font-bold text-slate-800">{totalQ}</span>
                    </div>
                    <div>
                      <span className="block text-[10px] text-slate-400 uppercase font-semibold">Duration</span>
                      <span className="font-bold text-slate-800">{test.durationMinutes || 120}m</span>
                    </div>
                    <div>
                      <span className="block text-[10px] text-slate-400 uppercase font-semibold">Marks</span>
                      <span className="font-bold text-slate-800">{test.totalMarks || (totalQ * 2)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50/80 px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] text-rose-700 font-semibold">
                    -0.66 Negative Marking
                  </span>

                  <button
                    onClick={() => navigate(`/student/prelims/test/${test._id}`)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-xs font-semibold shadow-xs hover:shadow-md transition-all"
                  >
                    <span>Start Test</span>
                    <Play size={13} fill="white" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
